<!--
  Register.svelte — New-account registration page.

  Collects first name, last name, email, and password.
  On success the server sends a verification email and the user
  is redirected to `/verify-account` to enter the code.

  Also supports Google, Apple and Microsoft sign-in for one-click registration.
-->
<script lang="ts">
	import { preventDefault } from 'svelte/legacy';

	import { onMount } from 'svelte';
	import { Button, Hr } from 'flowbite-svelte';
	import { push, querystring } from '../../abstract-spa-router';
	import { RoutedLink } from '../../components';
	import AuthLayout from './AuthLayout.svelte';
	import {
		FormField,
		PasswordField,
		AlertMessage,
		GoogleSignInButton,
		AppleSignInButton,
		MicrosoftSignInButton,
		LoadingOverlay
	} from '../../components';
	import { setToken, refreshTokenPeriodically } from '../../Auth';
	import { register, loginGoogle, loginApple, loginMicrosoft, isValidEmail } from '../../AppNetwork';
	import { configurationStore } from '../../AppConfiguration';
	import { t, t2 } from '../../i18n';

	// ── App context / invitation redirect ────────────────────
	/**
	 * On mount: redirect to the unified OTP login flow when arriving either
	 * from an app consent flow (`?app=UUID`) or from a legacy invitation
	 * email (`?emailToRegister=...`, built by earlier versions of
	 * BackCompatRouter.sendUserInvitation — new invitations link directly
	 * to /login with app + returnURL context).
	 *
	 * For legacy invitations we forward only the email to the login page so
	 * the invitee lands straight on the OTP form. The legacy `redirectURL`
	 * is dropped because it's a raw application URL, not a whitelisted
	 * returnURL — the user just ends up on the dashboard after sign-in.
	 */
	onMount(() => {
		const params = new URLSearchParams($querystring || '');
		const invitedEmail = params.get('emailToRegister');
		/** Legacy invitation link — forward just the email to the OTP login flow. */
		if (invitedEmail) {
			push(`/login?email=${encodeURIComponent(invitedEmail)}`);
			return;
		}
		/** App consent flow — unified OTP login handles both sign-up and sign-in. */
		if (params.get('app')) {
			push(`/login?${params.toString()}`);
		}
	});

	// ── Form state ────────────────────────────────────────────

	/** User's first name. */
	let firstName = $state('');
	/** User's last name. */
	let lastName = $state('');
	/** User's email address. */
	let email = $state('');
	/** Chosen password. */
	let password = $state('');
	/** Password confirmation (client-side check only). */
	let confirmPassword = $state('');
	/** Whether a network request is in progress. */
	let loading = $state(false);
	/** Top-level error message. */
	let errorMessage = $state('');

	// ── Field errors ──────────────────────────────────────────

	let firstNameError = $state('');
	let lastNameError = $state('');
	let emailError = $state('');
	let passwordError = $state('');
	let confirmPasswordError = $state('');

	// ── Validation ────────────────────────────────────────────

	/** Clear all field-level errors. */
	function clearErrors(): void {
		firstNameError = '';
		lastNameError = '';
		emailError = '';
		passwordError = '';
		confirmPasswordError = '';
		errorMessage = '';
	}

	/**
	 * Validate the registration form.
	 * @returns `true` when all fields are acceptable.
	 */
	function validate(): boolean {
		clearErrors();
		let valid = true;

		/** Validate first name is present. */
		if (!firstName.trim()) {
			firstNameError = t2('First name is required.');
			valid = false;
		}
		/** Validate last name is present. */
		if (!lastName.trim()) {
			lastNameError = t2('Last name is required.');
			valid = false;
		}
		/** Validate email is present and well-formed. */
		if (!email.trim()) {
			emailError = t2('Email is required.');
			valid = false;
		} else if (!isValidEmail(email.trim())) {
			emailError = t2('Please enter a valid email address.');
			valid = false;
		}

		/** Validate password meets minimum length requirement. */
		if (!password) {
			passwordError = t2('Password is required.');
			valid = false;
		} else if (password.length < 8) {
			passwordError = t2('Password must be at least 8 characters.');
			valid = false;
		}

		/** Ensure password and confirmation match. */
		if (password !== confirmPassword) {
			confirmPasswordError = t2('Passwords do not match.');
			valid = false;
		}

		return valid;
	}

	// ── Handlers ──────────────────────────────────────────────

	/**
	 * Complete login after successful registration or Google sign-in.
	 */
	function completeLogin(token: string): void {
		setToken(token);
		refreshTokenPeriodically();
		/* Cross-app navigation to the admin app. */
		window.location.href = '/Manage/';
	}

	/**
	 * Submit the registration form.
	 * On success, redirect to the email verification page.
	 */
	async function handleRegister(): Promise<void> {
		if (!validate()) return;

		loading = true;
		/** Send registration request and redirect to verification on success. */
		try {
			await register({
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				email: email.trim(),
				password
			});

			/*
			 * Registration returns the created user but no token.
			 * The account must be verified via an email link before login.
			 * Redirect to a "check your email" confirmation page.
			 */
			push(`/verify-account?email=${encodeURIComponent(email.trim())}`);
		} catch (err: any) {
			errorMessage = err?.message || t2('Registration failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the Google Sign-In credential.
	 * Google accounts are auto-registered, so we complete login directly.
	 */
	async function handleGoogleCredential(credential: string): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Authenticate via Google and complete login on success. */
		try {
			const result = await loginGoogle(credential);
			completeLogin(result.token);
		} catch (err: any) {
			errorMessage = err?.message || t2('Google sign-up failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the Sign in with Apple callback.
	 * Apple accounts are auto-registered, so we complete login directly.
	 *
	 * @param code - Authorization code from Apple's popup.
	 * @param name - Name Apple disclosed, present only on a first authorisation.
	 */
	async function handleAppleCredential(
		code: string,
		name?: { firstName?: string; lastName?: string }
	): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Authenticate via Apple and complete login on success. */
		try {
			const result = await loginApple(code, name);
			completeLogin(result.token);
		} catch (err: any) {
			errorMessage = err?.message || t2('Apple sign-up failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the Sign in with Microsoft callback.
	 * Microsoft accounts are auto-registered, so we complete login directly.
	 *
	 * @param code - Authorization code relayed from the popup callback page.
	 */
	async function handleMicrosoftCredential(code: string): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Authenticate via Microsoft and complete login on success. */
		try {
			const result = await loginMicrosoft(code);
			completeLogin(result.token);
		} catch (err: any) {
			errorMessage = err?.message || t2('Microsoft sign-up failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/** Handle Enter key submission. */
	function handleKeypress(event: KeyboardEvent): void {
		if (event.key === 'Enter') handleRegister();
	}
</script>

<AuthLayout heading={$t("Create an account")} subheading={$t("Get started in seconds — it's free.")}>
	{#if loading}
		<LoadingOverlay />
	{/if}

	{#if errorMessage}
		<AlertMessage type="error" message={errorMessage} onclose={() => (errorMessage = '')} />
	{/if}

	<form onsubmit={preventDefault(handleRegister)}>
		<div class="grid grid-cols-2 gap-4">
			<FormField
				id="reg-first-name"
				label={$t("First name")}
				placeholder={$t("John")}
				bind:value={firstName}
				error={firstNameError}
				required
				onkeypress={handleKeypress}
			/>
			<FormField
				id="reg-last-name"
				label={$t("Last name")}
				placeholder={$t("Doe")}
				bind:value={lastName}
				error={lastNameError}
				required
				onkeypress={handleKeypress}
			/>
		</div>

		<FormField
			id="reg-email"
			label={$t("Email")}
			type="email"
			placeholder={$t("you@example.com")}
			bind:value={email}
			error={emailError}
			required
			onkeypress={handleKeypress}
		/>

		<PasswordField
			id="reg-password"
			label={$t("Password")}
			placeholder={$t("At least 8 characters")}
			bind:value={password}
			error={passwordError}
			helper={$t("Must be at least 8 characters.")}
			required
			onkeypress={handleKeypress}
		/>

		<PasswordField
			id="reg-confirm-password"
			label={$t("Confirm password")}
			placeholder={$t("Re-enter your password")}
			bind:value={confirmPassword}
			error={confirmPasswordError}
			required
			onkeypress={handleKeypress}
		/>

		<Button type="submit" class="w-full" disabled={loading}>{$t("Create account")}</Button>
	</form>

	{#if $configurationStore?.googleClientId || $configurationStore?.appleClientId || $configurationStore?.microsoftClientId}
		<Hr class="my-6" />

		<!-- Social sign-in (auto-registers on first use) -->
		{#if $configurationStore?.googleClientId}
			<GoogleSignInButton oncredential={handleGoogleCredential} text="signup_with" />
		{/if}

		{#if $configurationStore?.appleClientId}
			<div class={$configurationStore?.googleClientId ? 'mt-3' : ''}>
				<AppleSignInButton oncredential={handleAppleCredential} text="signup_with" />
			</div>
		{/if}

		{#if $configurationStore?.microsoftClientId}
			<div class={$configurationStore?.googleClientId || $configurationStore?.appleClientId ? 'mt-3' : ''}>
				<MicrosoftSignInButton oncredential={handleMicrosoftCredential} text="signup_with" />
			</div>
		{/if}
	{/if}

	<!-- Footer: login link -->
	<div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
		{$t("Already have an account?")}
		<RoutedLink to="/login">
			<span class="font-medium text-primary-600 hover:underline dark:text-primary-400">{$t("Sign in")}</span>
		</RoutedLink>
	</div>
</AuthLayout>
